import React, { ReactNode, useState } from "react";
import { Button, Grid, styled } from "@mui/material";
import { Form, Formik, FormikProps, type FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";

import { FileWithPath } from "react-dropzone";

import { APIError } from "src/types";
import { Dropzone } from "src/components/Dropzone";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { fieldToError } from "src/utils/form.utils";
import { DropzoneSingleFileRequirementError } from "src/errors";

export type CaseShapeFormData = {
  caseShapefile: File | null;
};

interface Props {
  data: CaseShapeFormData;
  readOnly?: boolean;
  isLoading: boolean;
  errors?: APIError[] | null;
  formRef?: React.MutableRefObject<FormikProps<CaseShapeFormData> | null>;
  statusBody: ReactNode;
  resetErrorsOnChange?: boolean;
  onSubmit: (values: CaseShapeFormData) => void;
  onPrevious: () => void;
}

export function UploadCaseShapeForm(props: Props) {
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState(false);
  const initialValues: CaseShapeFormData = {
    caseShapefile: props.data.caseShapefile,
  };

  return render();

  function render() {
    return (
      <Formik
        innerRef={props.formRef}
        initialValues={initialValues}
        onSubmit={async (values, helpers: FormikHelpers<CaseShapeFormData>) => {
          setShowErrors(true);
          return props.onSubmit(values);
        }}
        enableReinitialize
      >
        {({ isSubmitting, isValid, dirty, setFieldValue, errors }) => (
          <Styles id="upload-case-shape-form">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Dropzone
                  className="dropzone"
                  dropzoneOptions={{
                    onDrop: (acceptedFiles: FileWithPath[]) => {
                      if (acceptedFiles.length > 1) throw DropzoneSingleFileRequirementError();
                      if (props.resetErrorsOnChange) setShowErrors(false);
                      setFieldValue("caseShapefile", acceptedFiles[0]);
                    },
                    accept: ["application/zip", "application/x-zip-compressed", "multipart/x-zip", ".zip"],
                    multiple: false,
                  }}
                />

                {errors.caseShapefile && (
                  <Grid item xs={12}>
                    <ServerErrors data={[fieldToError("caseShapefileError", errors.caseShapefile)]} />
                  </Grid>
                )}
              </Grid>

              {props.statusBody}

              {showErrors && props.errors && (
                <Grid item xs={12}>
                  <ServerErrors data={props.errors} />
                </Grid>
              )}
              {!props.readOnly && (
                <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                  <Button variant="text" onClick={props.onPrevious}>
                    {t("label.previous")}
                  </Button>
                  <ButtonLoading
                    isLoading={isSubmitting || props.isLoading}
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!(dirty && isValid)}
                  >
                    {t("label.next")}
                  </ButtonLoading>
                </ButtonGrid>
              )}
            </Grid>
          </Styles>
        )}
      </Formik>
    );
  }
}

const Styles = styled(Form)`
  .dropzone {
    & > div:nth-of-type(2) {
      padding-bottom: 0;
      margin-bottom: -16px;
    }
  }
`;
